import { ScreenSizeController } from '../../controllers/screen-size-controller';
import { html, TemplateResult, CSSResultGroup, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import componentStyles from './sic-section-separator.css.lit';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

/**
 * The section separator
 * @slot images required
 *
 * Lightdom styles in sic-section-separator.lightdom.css included in /shared.css
 */

@customElement('sic-section-separator')
export class SicSectionSeparator extends OutlineElement {
  screens = new ScreenSizeController(this);
  static styles: CSSResultGroup = [componentStyles];

  @state() hasAltTextSlot: boolean;

  animateScroll() {
    gsap.registerPlugin(ScrollTrigger);
    document.body.style.overflow = 'auto';
    document.scrollingElement?.scrollTo(0, 0);
    const sections = this.shadowRoot?.querySelectorAll('section');

    sections?.forEach((section, index) => {
      const w = section.querySelector('.wrapper');
      const [x, xEnd] = index === 0 ? ['-10%', '-50%'] : ['-50%', '-10%'];
      gsap.fromTo(
        w,
        { x },
        {
          x: xEnd,
          scrollTrigger: {
            trigger: section,
            scrub: 0.5,
          },
        }
      );
    });
  }

  firstUpdated() {
    this.hasAltTextSlot = this.querySelector('[slot="alt-text"]') !== null;
    // Tells if the user has enabled the "reduced motion" setting in their browser or OS.
    const isReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

    // Need to check if reduce motion is enabled, if so, don't animate
    !isReducedMotion.matches && this.animateScroll();
  }

  render(): TemplateResult {
    return html` <div role="group" aria-hidden="true">
        <section class="image-row-one">
          <div class="wrapper">
            <slot name="image-row-one"></slot>
          </div>
        </section>
        <section class="image-row-two">
          <div class="wrapper">
            <slot name="image-row-two"></slot>
          </div>
        </section>
      </div>
      ${this.hasAltTextSlot
        ? html`<div
            class="alt-text visually-hidden"
            id="section-separator-label"
          >
            <slot name="alt-text"></slot>
          </div>`
        : nothing}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sic-section-separator': SicSectionSeparator;
  }
}
